import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Settings, FileText, Zap, DollarSign, Users } from 'lucide-react';

interface FeatureItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const features: FeatureItemProps[] = [
  {
    icon: <FileText className="w-6 h-6 text-[#7f00ff]" />,
    title: 'Emissão de nota fiscal automática',
    description: 'Sua NF-e ou NFS-e geradas automaticamente na hora da venda, 100% livre de erros.'
  },
  {
    icon: <Layers className="w-6 h-6 text-[#7f00ff]" />,
    title: 'Gestão avançada de grade de variação',
    description: 'Controle cor, tamanho e modelo diretamente pelo painel e sincronize com os canais online.'
  },
  {
    icon: <DollarSign className="w-6 h-6 text-[#7f00ff]" />,
    title: 'Controle de cobranças',
    description: 'Acompanhe as datas de vencimento, controle crediários e fluxo de parcelamentos.'
  },
  {
    icon: <Zap className="w-6 h-6 text-[#7f00ff]" />,
    title: 'Relatórios de vendas personalizados',
    description: 'Crie filtros, explore cruzamento de dados e projete seu lucro em tempo real.'
  },
  {
    icon: <Users className="w-6 h-6 text-[#7f00ff]" />,
    title: 'Cálculo de comissões',
    description: 'Gere regras automáticas de comissão para a equipe da loja na hora do caixa bater.'
  },
  {
    icon: <Settings className="w-6 h-6 text-[#7f00ff]" />,
    title: 'Integração de hardware',
    description: 'Leitores de código de barras, impressoras e gavetas controladas plug and play.'
  }
];

export default function OlistFeatureGrid() {
  return (
    <section className="bg-white py-24 border-t border-slate-100">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-tight mb-4"
          >
            Funcionalidades que colocam seu <br className="hidden md:block"/>
            <span className="text-[#7f00ff]">negócio na velocidade máxima</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-500 max-w-2xl mx-auto"
          >
            O coração do seu negócio operando com processos consolidados, desde a venda física até o controle dos caixas.
          </motion.p>
        </div>

        {/* Feature Grid - Responsive 2 to 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {features.map((feature, idx) => (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              key={idx}
              className="flex flex-col gap-4 p-6 rounded-3xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100 shadow-sm hover:shadow-md"
            >
              <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center shrink-0">
                {feature.icon}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <button className="bg-[#7f00ff] hover:bg-[#5b21b6] text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
            Testar as funcionalidades agora
          </button>
        </div>
      </div>
    </section>
  );
}
